import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import {
  userRegistryData, userRoles, getRoleConfig, regionalRegistryMock,
  italianRegions, isValidFiscalCode
} from "@/data/usersRegistry";
import {
  UserCog, Plus, Pencil, Trash2, IdCard, Mail, Phone, MapPin, Stethoscope,
  Search, Globe2, CheckCircle2, AlertCircle, Filter
} from "lucide-react";

const emptyUser = {
  fiscalCode: "", firstName: "", lastName: "", birthDate: "", birthPlace: "", gender: "",
  email: "", phone: "", region: "", licenseNumber: "", specialization: "", roles: [],
  source: "manual", regionalSource: ""
};

const csvColumns = [
  { key: "id", label: "id" },
  { key: "fiscalCode", label: "codice_fiscale" },
  { key: "firstName", label: "nome" },
  { key: "lastName", label: "cognome" },
  { key: "birthDate", label: "data_nascita" },
  { key: "birthPlace", label: "luogo_nascita" },
  { key: "gender", label: "sesso" },
  { key: "email", label: "email" },
  { key: "phone", label: "telefono" },
  { key: "region", label: "regione" },
  { key: "licenseNumber", label: "numero_albo" },
  { key: "specialization", label: "specializzazione" },
  { key: "roles", label: "profili", get: (r) => (r.roles || []).join("|"), parse: (v) => v.split("|").map(s => s.trim()).filter(Boolean) },
  { key: "source", label: "origine" }
];

export const AdminUsersRegistryPage = () => {
  const [items, setItems] = useState(userRegistryData);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");

  // Regional import
  const [importOpen, setImportOpen] = useState(false);
  const [importRegion, setImportRegion] = useState("Lombardia");
  const [importCF, setImportCF] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(u => {
      if (filterRole !== "all" && !(u.roles || []).includes(filterRole)) return false;
      if (filterRegion !== "all" && u.region !== filterRegion) return false;
      if (q) {
        const hay = `${u.firstName} ${u.lastName} ${u.fiscalCode} ${u.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, filterRole, filterRegion]);

  // Deep-link from Admin home: ?action=new or ?action=import
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "new") {
      setEditing({ ...emptyUser, id: `u${Date.now()}`, createdAt: new Date().toISOString().slice(0, 10) });
      setEditOpen(true);
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    } else if (action === "import") {
      setImportOpen(true);
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const startEdit = (u) => {
    setEditing(u ? { ...u, roles: [...(u.roles || [])] } : { ...emptyUser, id: `u${Date.now()}`, createdAt: new Date().toISOString().slice(0, 10) });
    setEditOpen(true);
  };

  const save = () => {
    if (!editing.fiscalCode || !isValidFiscalCode(editing.fiscalCode)) {
      toast.error("Codice fiscale non valido", { description: "Inserisci un CF italiano di 16 caratteri." });
      return;
    }
    if (!editing.firstName || !editing.lastName) {
      toast.error("Nome e cognome obbligatori");
      return;
    }
    if (editing.roles.length === 0) {
      toast.error("Seleziona almeno un profilo");
      return;
    }
    setItems(prev => {
      const exists = prev.find(p => p.id === editing.id);
      return exists ? prev.map(p => p.id === editing.id ? editing : p) : [...prev, editing];
    });
    setEditOpen(false);
    toast.success("Utente salvato");
  };

  const remove = (id) => {
    setItems(prev => prev.filter(p => p.id !== id));
    toast.success("Utente rimosso");
  };

  const toggleRole = (rid) => {
    setEditing(e => ({
      ...e,
      roles: e.roles.includes(rid) ? e.roles.filter(x => x !== rid) : [...e.roles, rid]
    }));
  };

  // Regional registry simulated lookup
  const doRegistryLookup = () => {
    setImporting(true);
    setImportResult(null);
    setTimeout(() => {
      const pool = regionalRegistryMock[importRegion] || [];
      const cf = importCF.trim().toUpperCase();
      const found = cf ? pool.find(p => p.fiscalCode === cf) : pool[0];
      setImportResult(found
        ? { ok: true, data: found }
        : { ok: false, message: cf ? `Nessun medico trovato per CF "${cf}" in ${importRegion}` : `Nessun medico nel registro mock per ${importRegion}` });
      setImporting(false);
    }, 800);
  };

  const confirmImport = () => {
    if (!importResult?.ok) return;
    const d = importResult.data;
    // Dedupe by CF
    if (items.some(u => u.fiscalCode === d.fiscalCode)) {
      toast.error("Utente già censito", { description: `Il CF ${d.fiscalCode} è già presente.` });
      return;
    }
    const newUser = {
      ...emptyUser,
      ...d,
      id: `u${Date.now()}`,
      roles: ["doctor"],
      source: "regional_registry",
      regionalSource: `Albo Medici Regionale ${importRegion}`,
      createdAt: new Date().toISOString().slice(0, 10)
    };
    setItems(prev => [newUser, ...prev]);
    setImportOpen(false);
    setImportResult(null);
    setImportCF("");
    toast.success("Utente importato dall'albo regionale", { description: `${d.firstName} ${d.lastName} (${d.specialization})` });
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <UserCog className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Configurazione · Censimento utenti</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Utenti piattaforma</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} utenti totali · 5 profili supportati</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CsvIOButtons rows={items} columns={csvColumns} filename="utenti" onImport={(p) => setItems(p.map(x => ({ ...emptyUser, ...x, roles: Array.isArray(x.roles) ? x.roles : [] })))} testIdPrefix="users-registry-csv" />
          <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportCF(""); }} className="gap-2" data-testid="users-registry-import-regional-btn">
            <Globe2 className="h-4 w-4" /> Importa da albo regionale
          </Button>
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="users-registry-new-btn">
            <Plus className="h-4 w-4" /> Nuovo utente
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-muted/20">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca per nome, CF o email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs h-8"
          data-testid="users-registry-search"
        />
        <Filter className="h-4 w-4 text-muted-foreground ml-2" />
        <select className="border rounded-md p-1.5 text-sm bg-background" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} data-testid="users-registry-filter-role">
          <option value="all">Tutti i profili</option>
          {userRoles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <select className="border rounded-md p-1.5 text-sm bg-background" value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} data-testid="users-registry-filter-region">
          <option value="all">Tutte le regioni</option>
          {italianRegions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} di {items.length}</span>
      </div>

      {/* User cards */}
      <div className="grid gap-3 md:grid-cols-2">
        {filtered.map(u => (
          <Card key={u.id} data-testid={`user-registry-card-${u.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className="text-base">{u.firstName} {u.lastName}</CardTitle>
                  <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{u.fiscalCode}</p>
                </div>
                {u.source === "regional_registry" && (
                  <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 gap-1 text-[10px] shrink-0">
                    <Globe2 className="h-3 w-3" /> Albo regionale
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {(u.roles || []).map(rid => {
                  const r = getRoleConfig(rid);
                  return r ? <Badge key={rid} variant="outline" className={`${r.color} text-[10px]`}>{r.label}</Badge> : null;
                })}
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                {u.email && <p className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {u.email}</p>}
                {u.phone && <p className="inline-flex items-center gap-1 truncate"><Phone className="h-3 w-3" /> {u.phone}</p>}
                {u.region && <p className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {u.region}</p>}
                {u.specialization && <p className="inline-flex items-center gap-1"><Stethoscope className="h-3 w-3" /> {u.specialization}{u.licenseNumber ? ` · Albo ${u.licenseNumber}` : ""}</p>}
              </div>
              <div className="flex justify-end gap-1 pt-2 border-t">
                <Button variant="ghost" size="icon" onClick={() => startEdit(u)} data-testid={`user-registry-edit-${u.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(u.id)} data-testid={`user-registry-remove-${u.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card className="md:col-span-2 border-dashed">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">Nessun utente corrisponde ai filtri.</CardContent>
          </Card>
        )}
      </div>

      {/* Edit/New manual dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && items.find(p => p.id === editing.id) ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
            <DialogDescription>Censimento manuale a partire dal codice fiscale e dati anagrafici.</DialogDescription>
          </DialogHeader>
          {editing && (
            <Tabs defaultValue="identity" className="space-y-3">
              <TabsList>
                <TabsTrigger value="identity" data-testid="user-form-tab-identity">Anagrafica</TabsTrigger>
                <TabsTrigger value="contact" data-testid="user-form-tab-contact">Contatti</TabsTrigger>
                <TabsTrigger value="roles" data-testid="user-form-tab-roles">Profili</TabsTrigger>
              </TabsList>

              <TabsContent value="identity" className="space-y-3">
                <div>
                  <Label htmlFor="cf">Codice Fiscale</Label>
                  <Input
                    id="cf"
                    value={editing.fiscalCode}
                    onChange={(e) => setEditing({ ...editing, fiscalCode: e.target.value.toUpperCase() })}
                    placeholder="RSSMRA80A01H501Z"
                    maxLength={16}
                    className={editing.fiscalCode && !isValidFiscalCode(editing.fiscalCode) ? "border-rose-300" : ""}
                    data-testid="user-form-fiscalCode"
                  />
                  {editing.fiscalCode && !isValidFiscalCode(editing.fiscalCode) && (
                    <p className="text-xs text-rose-600 mt-1 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Formato non valido (16 caratteri alfanumerici)</p>
                  )}
                  {editing.fiscalCode && isValidFiscalCode(editing.fiscalCode) && (
                    <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> CF valido</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nome</Label>
                    <Input value={editing.firstName} onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} data-testid="user-form-firstName" />
                  </div>
                  <div>
                    <Label>Cognome</Label>
                    <Input value={editing.lastName} onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} data-testid="user-form-lastName" />
                  </div>
                  <div>
                    <Label>Data di nascita</Label>
                    <Input type="date" value={editing.birthDate} onChange={(e) => setEditing({ ...editing, birthDate: e.target.value })} />
                  </div>
                  <div>
                    <Label>Luogo di nascita</Label>
                    <Input value={editing.birthPlace} onChange={(e) => setEditing({ ...editing, birthPlace: e.target.value })} />
                  </div>
                  <div>
                    <Label>Sesso</Label>
                    <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background" value={editing.gender} onChange={(e) => setEditing({ ...editing, gender: e.target.value })}>
                      <option value="">—</option><option value="M">M</option><option value="F">F</option><option value="X">Altro/Non specificato</option>
                    </select>
                  </div>
                  <div>
                    <Label>Regione</Label>
                    <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background" value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })}>
                      <option value="">—</option>
                      {italianRegions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="contact" className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-form-email" /></div>
                  <div><Label>Telefono</Label><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
                  <div><Label>N° iscrizione albo</Label><Input value={editing.licenseNumber} onChange={(e) => setEditing({ ...editing, licenseNumber: e.target.value })} placeholder="Solo per medici" /></div>
                  <div><Label>Specializzazione</Label><Input value={editing.specialization} onChange={(e) => setEditing({ ...editing, specialization: e.target.value })} placeholder="Cardiologia, Pneumologia..." /></div>
                </div>
              </TabsContent>

              <TabsContent value="roles" className="space-y-3">
                <p className="text-xs text-muted-foreground">Un utente può ricoprire più profili contemporaneamente (es. medico + responsabile struttura).</p>
                <div className="grid gap-2 md:grid-cols-2">
                  {userRoles.map(r => (
                    <label key={r.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${editing.roles.includes(r.id) ? "bg-primary/5 border-primary" : "hover:bg-muted/50"}`} data-testid={`user-form-role-${r.id}`}>
                      <Checkbox checked={editing.roles.includes(r.id)} onCheckedChange={() => toggleRole(r.id)} />
                      <Badge variant="outline" className={r.color}>{r.label}</Badge>
                    </label>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="user-form-save">Salva utente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regional registry import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportResult(null); setImportCF(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe2 className="h-5 w-5 text-cyan-600" /> Importa da albo medici regionale
            </DialogTitle>
            <DialogDescription>
              Cerca un medico specialista nei sistemi regionali integrati (Lombardia, Piemonte, Emilia-Romagna, Lazio).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Regione</Label>
              <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background" value={importRegion} onChange={(e) => { setImportRegion(e.target.value); setImportResult(null); }} data-testid="import-region-select">
                {Object.keys(regionalRegistryMock).map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label>Codice Fiscale (opzionale)</Label>
              <Input
                placeholder="Lascia vuoto per primo risultato mock"
                value={importCF}
                onChange={(e) => setImportCF(e.target.value.toUpperCase())}
                maxLength={16}
                data-testid="import-cf-input"
              />
            </div>
            <Button onClick={doRegistryLookup} disabled={importing} className="w-full gap-2" data-testid="import-search-btn">
              <Search className="h-4 w-4" />
              {importing ? "Ricerca in corso…" : "Cerca nell'albo"}
            </Button>

            {importResult?.ok && (
              <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/50 space-y-1.5" data-testid="import-result-ok">
                <p className="font-semibold text-sm inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Medico trovato</p>
                <div className="text-sm">
                  <p><span className="font-medium">{importResult.data.firstName} {importResult.data.lastName}</span></p>
                  <p className="text-xs text-muted-foreground font-mono">{importResult.data.fiscalCode}</p>
                  <p className="text-xs text-muted-foreground">Albo {importResult.data.licenseNumber} · {importResult.data.specialization}</p>
                  <p className="text-xs text-muted-foreground">{importResult.data.email}</p>
                </div>
              </div>
            )}
            {importResult && !importResult.ok && (
              <div className="p-3 rounded-lg border border-rose-200 bg-rose-50/50 text-sm inline-flex items-center gap-2 w-full">
                <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
                {importResult.message}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Chiudi</Button>
            <Button onClick={confirmImport} disabled={!importResult?.ok} data-testid="import-confirm-btn">Importa nel censimento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersRegistryPage;
